/**
 * Knowledge Base (M11): YouTrack's layout — a tree of articles per project on the
 * left, the article (or a new-article editor) on the right. The tree is one
 * `GET /api/articles` (no content); the server's `article` WebSocket notice
 * makes open pages refetch, so another person's edit shows up without live
 * queries. Drafts exist only for unpublished articles, like YouTrack.
 */
import { articleKey, Permission, type Article, type ArticleComment, type ArticleDraft, type ArticleHit, type ArticleNode, type Visibility } from "@gjwtrack/domain";
import { ChevronDown, ChevronRight, Copy, Ellipsis, FileText, FolderInput, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ApiError, uuid } from "@gjwtrack/client";
import { Centered } from "../App.tsx";
import { Attachments } from "../components/Attachments.tsx";
import { Avatar } from "../components/Badges.tsx";
import { MarkdownEditor } from "../components/MarkdownEditor.tsx";
import { MarkdownView } from "../components/MarkdownView.tsx";
import { TopBar } from "../components/TopBar.tsx";
import { VisibilityMenu } from "../components/VisibilityMenu.tsx";
import { client, useClient } from "../lib/client.ts";
import { absTime, can, relTimeAgo, userName } from "../lib/format.ts";
import { Menu } from "../ui/Menu.tsx";
import { Button, Dialog, DialogContent, DialogHeader, Input, InputSize, Panel } from "../ui/ring.ts";
import { SimpleSelect } from "../ui/SimpleSelect.tsx";
import { copyText } from "../lib/copyIssue.ts";

type View = "home" | "article" | "new";

interface Tree {
  nodes: ArticleNode[];
  byId: Map<number, ArticleNode>;
  /** parentId (null = project root) → children in ordinal order, per project. */
  children: Map<string, ArticleNode[]>;
  projectIds: number[];
}

const childKey = (projectId: number, parentId: number | null): string => `${projectId}:${parentId ?? "root"}`;

function buildTree(nodes: ArticleNode[]): Tree {
  const byId = new Map<number, ArticleNode>();
  const children = new Map<string, ArticleNode[]>();
  const projectIds: number[] = [];
  for (const n of nodes) byId.set(n.id, n);
  for (const n of nodes) {
    // A parent the reader cannot see: show the child at the root so nothing is lost.
    const parent = n.parentId !== null && byId.has(n.parentId) ? n.parentId : null;
    const k = childKey(n.projectId, parent);
    const list = children.get(k) ?? [];
    list.push(n);
    children.set(k, list);
    if (!projectIds.includes(n.projectId)) projectIds.push(n.projectId);
  }
  for (const list of children.values()) list.sort((a, b) => a.ordinal - b.ordinal || a.id - b.id);
  return { nodes, byId, children, projectIds };
}

/** Ancestors from the root down to (excluding) the node. */
function pathTo(tree: Tree, node: ArticleNode): ArticleNode[] {
  const out: ArticleNode[] = [];
  let cur = node.parentId !== null ? tree.byId.get(node.parentId) : undefined;
  while (cur && out.length < 50) {
    out.unshift(cur);
    cur = cur.parentId !== null ? tree.byId.get(cur.parentId) : undefined;
  }
  return out;
}

function subtreeSize(tree: Tree, node: ArticleNode): number {
  let n = 0;
  const stack = [node];
  while (stack.length) {
    const cur = stack.pop()!;
    n++;
    for (const c of tree.children.get(childKey(cur.projectId, cur.id)) ?? []) stack.push(c);
  }
  return n;
}

export function KnowledgeBasePage({ view }: { view: View }) {
  const navigate = useNavigate();
  const { key } = useParams<{ key: string }>();
  const [params] = useSearchParams();
  const schema = useClient((s) => s.schema);
  const me = useClient((s) => s.me);
  const tick = useClient((s) => s.articleTick);
  const [nodes, setNodes] = useState<ArticleNode[] | null>(null);
  const [drafts, setDrafts] = useState<ArticleDraft[]>([]);
  const tree = useMemo(() => buildTree(nodes ?? []), [nodes]);

  const reloadTree = useCallback((): Promise<void> => client.http.articles().then(setNodes).catch((e) => client.pushError("articles", (e as Error).message)), []);
  const reloadDrafts = useCallback((): Promise<void> => client.http.articleDrafts().then(setDrafts).catch(() => {}), []);
  useEffect(() => {
    void reloadTree();
    void reloadDrafts();
  }, [reloadTree, reloadDrafts]);
  // Any article event: refetch the tree (a few tens of KB, only on Knowledge Base changes).
  useEffect(() => {
    if (tick) void reloadTree();
  }, [tick, reloadTree]);

  const projects = (schema?.data.projects ?? []).filter((p) => can(me, p.id, Permission.CREATE_ARTICLE));
  const newArticle = (opts: { projectId?: number; parentId?: number | null } = {}): void => {
    const q = new URLSearchParams();
    if (opts.projectId !== undefined) q.set("project", String(opts.projectId));
    if (opts.parentId) q.set("parent", String(opts.parentId));
    navigate(`/articles/new${q.size ? `?${q}` : ""}`);
  };

  const current = view === "article" && key ? tree.nodes.find((n) => schema && articleKey(schema.projectsById.get(n.projectId)?.key ?? "", n.number).toUpperCase() === key.toUpperCase()) : undefined;
  const crumbs = [{ label: "Knowledge Base", to: "/articles" }];
  if (current && schema) {
    const p = schema.projectsById.get(current.projectId);
    crumbs.push({ label: p?.name ?? String(current.projectId), to: `/articles` });
    for (const a of pathTo(tree, current)) crumbs.push({ label: a.summary, to: `/article/${articleKey(p?.key ?? "", a.number)}` });
  }

  return (
    <div className="flex h-full flex-col">
      <TopBar
        crumbs={view === "new" ? [...crumbs, { label: "New article" }] : crumbs}
        query=""
        onQuery={(q) => navigate(q ? `/issues?q=${encodeURIComponent(q)}` : "/issues")}
        onCreate={() => navigate("/issues")}
        right={
          projects.length > 0 ? (
            <Button icon={Plus} onClick={() => newArticle(current ? { projectId: current.projectId } : {})} title="New article">
              New article
            </Button>
          ) : undefined
        }
      />
      <div className="flex min-h-0 flex-1">
        <ArticleTree tree={tree} loading={nodes === null} drafts={drafts} currentId={current?.id} onNew={newArticle} onDraftDeleted={reloadDrafts} />
        <div className="min-w-0 flex-1 overflow-auto">
          {view === "home" && <Home tree={tree} loading={nodes === null} onNew={newArticle} canCreate={projects.length > 0} />}
          {view === "article" && key && <ArticleView articleKey={key} tree={tree} onNewChild={(a) => newArticle({ projectId: a.projectId, parentId: a.id })} onChanged={reloadTree} />}
          {view === "new" && (
            <NewArticle
              tree={tree}
              projects={projects}
              initialProjectId={params.get("project") ? Number(params.get("project")) : null}
              initialParentId={params.get("parent") ? Number(params.get("parent")) : null}
              draftId={params.get("draft") ? Number(params.get("draft")) : null}
              drafts={drafts}
              onDraftsChanged={reloadDrafts}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- tree

function ArticleTree({ tree, loading, drafts, currentId, onNew, onDraftDeleted }: { tree: Tree; loading: boolean; drafts: ArticleDraft[]; currentId: number | undefined; onNew: (o: { projectId?: number; parentId?: number | null }) => void; onDraftDeleted: () => void }) {
  const schema = useClient((s) => s.schema);
  const me = useClient((s) => s.me);
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<ArticleHit[] | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem("gjw.kb.open") ?? "{}") as Record<string, boolean>;
    } catch {
      return {};
    }
  });
  const toggle = (k: string, force?: boolean): void =>
    setOpen((o) => {
      const next = { ...o, [k]: force ?? !(o[k] ?? false) };
      try {
        localStorage.setItem("gjw.kb.open", JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  // The path to the shown article is always open.
  useEffect(() => {
    if (currentId === undefined) return;
    const node = tree.byId.get(currentId);
    if (!node) return;
    setOpen((o) => {
      const next = { ...o, [`p${node.projectId}`]: true };
      for (const a of pathTo(tree, node)) next[`a${a.id}`] = true;
      return next;
    });
  }, [currentId, tree]);

  // Search: server-side substring over summary and content, 300 ms after typing stops.
  useEffect(() => {
    const text = q.trim();
    if (!text) {
      setHits(null);
      return;
    }
    const timer = setTimeout(() => {
      client.http
        .searchArticles(text)
        .then(setHits)
        .catch(() => setHits([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  const keyOf = (n: ArticleNode): string => articleKey(schema?.projectsById.get(n.projectId)?.key ?? String(n.projectId), n.number);

  const renderNode = (n: ArticleNode, depth: number): ReactNode => {
    const kids = tree.children.get(childKey(n.projectId, n.id)) ?? [];
    const k = `a${n.id}`;
    const isOpen = open[k] ?? false;
    return (
      <li key={n.id}>
        <div className={`group flex items-center gap-1 rounded pr-1 text-[14px] hover:bg-sunken ${n.id === currentId ? "bg-surface" : ""}`} style={{ paddingLeft: `${depth * 14 + 4}px` }}>
          <button className={`flex h-6 w-5 shrink-0 items-center justify-center text-muted ${kids.length ? "" : "invisible"}`} onClick={() => toggle(k)} aria-label={isOpen ? "Collapse" : "Expand"}>
            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          <Link to={`/article/${keyOf(n)}`} className="min-w-0 flex-1 truncate py-1" title={n.summary}>
            {n.summary}
          </Link>
          {can(me, n.projectId, Permission.CREATE_ARTICLE) && (
            <button className="icon-btn h-5 w-5 opacity-0 group-hover:opacity-100" title="New sub-article" onClick={() => onNew({ projectId: n.projectId, parentId: n.id })}>
              <Plus size={12} />
            </button>
          )}
        </div>
        {isOpen && kids.length > 0 && <ul>{kids.map((c) => renderNode(c, depth + 1))}</ul>}
      </li>
    );
  };

  return (
    <aside className="flex w-72 shrink-0 flex-col border-r border-line" aria-label="Articles">
      <div className="relative m-2">
        <Search size={14} className="pointer-events-none absolute top-2 left-2 text-muted" />
        <input className="field w-full !pl-7 text-[13px]" placeholder="Search articles" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search articles" />
        {q && (
          <button className="absolute top-1.5 right-1.5 text-muted hover:text-ink" onClick={() => setQ("")} title="Clear">
            <X size={14} />
          </button>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {hits !== null ? (
          <ul className="flex flex-col gap-1">
            {hits.length === 0 && <li className="px-2 py-1 text-muted">No articles match.</li>}
            {hits.map((h) => (
              <li key={h.id}>
                <Link to={`/article/${keyOf(h)}`} className="block rounded px-2 py-1.5 hover:bg-sunken" onClick={() => setQ("")}>
                  <div className="truncate text-[14px]">{h.summary}</div>
                  <div className="truncate text-xs text-muted">
                    {schema?.projectsById.get(h.projectId)?.key ?? h.projectId} · {h.snippet || "(no content)"}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <>
            {drafts.length > 0 && (
              <div className="mb-1">
                <button className="flex w-full items-center gap-1 px-1 py-1.5 text-[15px] text-ink" onClick={() => toggle("drafts")}>
                  {(open.drafts ?? true) ? <ChevronDown size={16} className="text-muted" /> : <ChevronRight size={16} className="text-muted" />}
                  Drafts <span className="ml-1 text-xs text-muted">{drafts.length}</span>
                </button>
                {(open.drafts ?? true) && (
                  <ul className="pl-5">
                    {drafts.map((d) => (
                      <li key={d.id} className="group flex items-center gap-1 rounded pr-1 hover:bg-sunken">
                        <FileText size={14} className="shrink-0 text-muted" />
                        <button className="min-w-0 flex-1 truncate py-1 text-left text-[14px]" onClick={() => navigate(`/articles/new?draft=${d.id}`)}>
                          {d.summary || "(untitled)"}
                        </button>
                        <button
                          className="icon-btn h-5 w-5 opacity-0 group-hover:opacity-100"
                          title="Discard draft"
                          onClick={() => confirm("Discard this draft?") && void client.http.command("deleteArticleDraft", { draftId: d.id }, uuid()).then(onDraftDeleted)}
                        >
                          <X size={12} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {loading && <div className="px-2 py-1 text-muted">Loading…</div>}
            {!loading && tree.projectIds.length === 0 && <div className="px-2 py-1 text-muted">No articles yet.</div>}
            {tree.projectIds.map((pid) => {
              const p = schema?.projectsById.get(pid);
              const k = `p${pid}`;
              const isOpen = open[k] ?? true;
              const roots = tree.children.get(childKey(pid, null)) ?? [];
              return (
                <div key={pid} className="mb-1">
                  <div className="group flex items-center gap-1 pr-1">
                    <button className="flex min-w-0 flex-1 items-center gap-1 px-1 py-1.5 text-[15px] text-ink" onClick={() => toggle(k)}>
                      {isOpen ? <ChevronDown size={16} className="text-muted" /> : <ChevronRight size={16} className="text-muted" />}
                      <span className="truncate">{p?.name ?? pid}</span>
                      <span className="ml-1 text-xs text-muted">{roots.length}</span>
                    </button>
                    {can(me, pid, Permission.CREATE_ARTICLE) && (
                      <button className="icon-btn h-5 w-5 opacity-0 group-hover:opacity-100" title="New article in this project" onClick={() => onNew({ projectId: pid, parentId: null })}>
                        <Plus size={12} />
                      </button>
                    )}
                  </div>
                  {isOpen && <ul>{roots.map((n) => renderNode(n, 1))}</ul>}
                </div>
              );
            })}
          </>
        )}
      </div>
    </aside>
  );
}

// ----------------------------------------------------------------- home

function Home({ tree, loading, onNew, canCreate }: { tree: Tree; loading: boolean; onNew: (o: { projectId?: number }) => void; canCreate: boolean }) {
  const schema = useClient((s) => s.schema);
  if (loading) return <Centered>Loading…</Centered>;
  const recent = [...tree.nodes].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 12);
  return (
    <div className="px-8 py-5">
      <div className="mb-4 flex items-center gap-3">
        <h1 className="text-xl font-semibold tracking-tight">Knowledge Base</h1>
        <div className="flex-1" />
        {canCreate && (
          <Button primary icon={Plus} onClick={() => onNew({})}>
            New article
          </Button>
        )}
      </div>
      {tree.nodes.length === 0 ? (
        <div className="text-muted">No articles yet. {canCreate ? "Write the first one: a team handbook, a runbook, a design note." : "Nobody has written any you can see."}</div>
      ) : (
        <>
          <div className="mb-2 text-[13px] text-muted">Recently updated</div>
          <ul className="grid max-w-4xl grid-cols-2 gap-3">
            {recent.map((n) => {
              const p = schema?.projectsById.get(n.projectId);
              return (
                <li key={n.id}>
                  <Link to={`/article/${articleKey(p?.key ?? "", n.number)}`} className="block rounded-lg border border-line bg-surface p-4 hover:border-accent">
                    <div className="mb-1 truncate text-[15px] font-semibold">{n.summary}</div>
                    <div className="text-xs text-muted">
                      {p?.name ?? n.projectId} · {articleKey(p?.key ?? "", n.number)} · updated {relTimeAgo(n.updatedAt)}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- article

function ArticleView({ articleKey: key, tree, onNewChild, onChanged }: { articleKey: string; tree: Tree; onNewChild: (a: Article) => void; onChanged: () => void }) {
  const schema = useClient((s) => s.schema);
  const me = useClient((s) => s.me);
  const tick = useClient((s) => s.articleTick);
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [stale, setStale] = useState(false);

  const load = useCallback((): Promise<void> => {
    return client.http
      .articleByKey(key)
      .then((a) => {
        setArticle(a);
        setError(null);
        setStale(false);
      })
      .catch((e: unknown) => setError(e instanceof ApiError && e.status === 404 ? "No such article, or you cannot see it." : (e as Error).message));
  }, [key]);
  useEffect(() => {
    setEditing(false);
    void load();
  }, [load]);
  // Someone changed this article: refetch unless we are mid-edit (then say so and let them decide).
  useEffect(() => {
    if (!tick || !article || tick.id !== article.id) return;
    if (tick.kind === "deleted") {
      navigate("/articles");
      return;
    }
    if (tick.version === article.version && tick.kind === "updated" && !editing) {
      // Comment / attachment touch: same version, refetch is cheap.
      void load();
      return;
    }
    if (editing) setStale(true);
    else void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  if (error) return <Centered>{error}</Centered>;
  if (!article || !schema) return <Centered>Loading…</Centered>;

  const project = schema.projectsById.get(article.projectId);
  const k = articleKey(project?.key ?? "", article.number);
  const editable = !!me && (me.isAdmin || me.id === article.authorId || can(me, article.projectId, Permission.UPDATE_ARTICLE));
  const deletable = !!me && (me.isAdmin || me.id === article.authorId || can(me, article.projectId, Permission.DELETE_ARTICLE));
  const node = tree.byId.get(article.id);
  const children = tree.children.get(childKey(article.projectId, article.id)) ?? [];

  const save = async (patch: { summary?: string; content?: string | null; visibility?: Visibility | null }): Promise<boolean> => {
    try {
      const next = await client.http.command<Article>("updateArticle", { articleId: article.id, expectedVersion: article.version, patch }, uuid());
      setArticle(next);
      setStale(false);
      return true;
    } catch (e: unknown) {
      if (e instanceof ApiError && e.status === 409) {
        client.pushError("conflict", "Someone else changed this article. Reload to see their version; your text is kept in the editor.");
        setStale(true);
      } else client.pushError(e instanceof ApiError ? e.code : "network", (e as Error).message);
      return false;
    }
  };
  const remove = async (): Promise<void> => {
    const n = node ? subtreeSize(tree, node) : 1;
    if (!confirm(n > 1 ? `Delete "${article.summary}" and its ${n - 1} sub-article${n > 2 ? "s" : ""}?` : `Delete "${article.summary}"?`)) return;
    try {
      await client.http.command("deleteArticle", { articleId: article.id }, uuid());
      onChanged();
      navigate("/articles");
    } catch (e: unknown) {
      client.pushError(e instanceof ApiError ? e.code : "network", (e as Error).message);
    }
  };

  return (
    <div className="px-8 py-4" data-article-id={article.id} data-article-key={k}>
      <div className="mb-3 flex items-center gap-4 text-[13.5px] whitespace-nowrap text-muted">
        <span>
          Created by <span className="text-value">{userName(schema, article.authorId)}</span> <span title={absTime(article.createdAt)}>{relTimeAgo(article.createdAt)}</span>
        </span>
        {article.updatedBy !== null && article.updatedAt !== article.createdAt && (
          <span>
            Updated by <span className="text-value">{userName(schema, article.updatedBy)}</span> <span title={absTime(article.updatedAt)}>{relTimeAgo(article.updatedAt)}</span>
          </span>
        )}
        <span className="font-mono">{k}</span>
        <span className="flex-1" />
        <VisibilityMenu noun="article" visibility={article.visibility} editable={editable} onChange={(v) => void save({ visibility: v })} />
      </div>
      {stale && (
        <div className="mb-3 flex items-center gap-3 rounded border border-line bg-sunken px-3 py-2 text-[13px]">
          This article was changed by someone else.
          <Button onClick={() => void load()}>Reload</Button>
        </div>
      )}
      <div className="mb-3 flex items-start gap-3">
        <InlineTitle value={article.summary} editable={editable} onCommit={(summary) => void save({ summary })} />
        <div className="flex shrink-0 items-center gap-1 pt-1">
          <Button icon={Pencil} title="Edit" onClick={() => setEditing(true)} disabled={!editable || editing} />
          <Menu
            anchor={<Button icon={Ellipsis} title="More" />}
            items={[
              { key: "link", label: "Copy link", icon: Copy, onClick: () => void copyText(`${location.origin}/article/${k}`) },
              { key: "id", label: "Copy article ID", icon: Copy, onClick: () => void copyText(k) },
              ...(editable ? [{ key: "child", label: "New sub-article", icon: Plus, onClick: () => onNewChild(article) }, { key: "move", label: "Move…", icon: FolderInput, onClick: () => setMoving(true) }] : []),
              ...(deletable ? [{ key: "delete", label: "Delete", icon: Trash2, onClick: () => void remove() }] : []),
            ]}
          />
        </div>
      </div>

      <section className="mb-8">
        {editing ? (
          <MarkdownEditor
            initial={article.content ?? ""}
            placeholder="Write the article…"
            autoFocus
            target={{ articleId: article.id }}
            onSave={async (md) => {
              if ((md || null) === article.content || (await save({ content: md || null }))) setEditing(false);
            }}
            onCancel={() => setEditing(false)}
          />
        ) : article.content ? (
          <div className={editable ? "cursor-text" : ""} onDoubleClick={() => editable && setEditing(true)} title={editable ? "Double-click to edit" : undefined}>
            <MarkdownView text={article.content} target={{ articleId: article.id }} className="text-[15px]" />
          </div>
        ) : (
          <button className="text-faint hover:text-muted" onClick={() => editable && setEditing(true)} disabled={!editable}>
            Empty article. {editable && "Click to write it."}
          </button>
        )}
      </section>

      {children.length > 0 && (
        <section className="mb-8">
          <div className="mb-2 text-[13px] text-muted">Sub-articles</div>
          <ul className="flex flex-col gap-1">
            {children.map((c) => (
              <li key={c.id}>
                <Link to={`/article/${articleKey(project?.key ?? "", c.number)}`} className="text-link hover:underline">
                  {c.summary}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Attachments target={{ articleId: article.id }} canAdd={editable} />
      <ArticleComments article={article} canComment={can(me, article.projectId, Permission.ADD_COMMENT)} />
      {moving && <MoveDialog article={article} tree={tree} onClose={() => setMoving(false)} onMoved={(a) => setArticle(a)} />}
    </div>
  );
}

function InlineTitle({ value, editable, onCommit }: { value: string; editable: boolean; onCommit: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);
  const finish = (commit: boolean): void => {
    setEditing(false);
    const v = draft.trim();
    if (commit && v && v !== value) onCommit(v);
  };
  if (!editing) {
    return (
      <h1 className={`min-w-0 flex-1 text-[26px] leading-tight font-semibold tracking-tight text-balance ${editable ? "cursor-text rounded hover:bg-surface" : ""}`} onClick={() => editable && setEditing(true)} title={editable ? "Click to edit" : undefined}>
        {value}
      </h1>
    );
  }
  return (
    <input
      className="field w-full text-[26px] leading-tight font-semibold tracking-tight"
      aria-label="Article title"
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => finish(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter") finish(true);
        if (e.key === "Escape") finish(false);
      }}
    />
  );
}

/** Pick a new parent (or the project root); the article goes last among its new siblings. */
function MoveDialog({ article, tree, onClose, onMoved }: { article: Article; tree: Tree; onClose: () => void; onMoved: (a: Article) => void }) {
  const [parentId, setParentId] = useState<string>(article.parentId === null ? "" : String(article.parentId));
  const [busy, setBusy] = useState(false);
  const node = tree.byId.get(article.id);
  // Everything in the project except the article's own subtree.
  const excluded = new Set<number>();
  if (node) {
    const stack = [node];
    while (stack.length) {
      const cur = stack.pop()!;
      excluded.add(cur.id);
      for (const c of tree.children.get(childKey(cur.projectId, cur.id)) ?? []) stack.push(c);
    }
  }
  const options: { value: string; label: string }[] = [{ value: "", label: "(top level)" }];
  const walk = (parent: number | null, depth: number): void => {
    for (const c of tree.children.get(childKey(article.projectId, parent)) ?? []) {
      if (excluded.has(c.id)) continue;
      options.push({ value: String(c.id), label: `${"  ".repeat(depth)}${c.summary}` });
      walk(c.id, depth + 1);
    }
  };
  walk(null, 0);
  const submit = async (): Promise<void> => {
    setBusy(true);
    try {
      const a = await client.http.command<Article>("moveArticle", { articleId: article.id, parentId: parentId ? Number(parentId) : null, ordinal: 1_000_000 }, uuid());
      onMoved(a);
      onClose();
    } catch (e: unknown) {
      client.pushError(e instanceof ApiError ? e.code : "network", (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog show label="Move article" onCloseAttempt={onClose} trapFocus autoFocusFirst={false} contentClassName="!w-[30rem] !max-w-[calc(100vw-2rem)]">
      <DialogHeader>Move article</DialogHeader>
      <DialogContent>
        <label className="block">
          <span className="mb-1 block text-xs text-muted">Parent article</span>
          <SimpleSelect full value={parentId} options={options} onChange={setParentId} />
        </label>
      </DialogContent>
      <Panel>
        <Button primary onClick={() => void submit()} disabled={busy}>
          Move
        </Button>
        <Button onClick={onClose}>Cancel</Button>
      </Panel>
    </Dialog>
  );
}

// ----------------------------------------------------------------- comments

function ArticleComments({ article, canComment }: { article: Article; canComment: boolean }) {
  const schema = useClient((s) => s.schema);
  const me = useClient((s) => s.me);
  const [comments, setComments] = useState<ArticleComment[] | null>(null);
  const [composing, setComposing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  // Comments touch the article (updated_at), so a changed `updatedAt` means "refetch".
  useEffect(() => {
    client.http
      .articleComments(article.id)
      .then(setComments)
      .catch(() => setComments([]));
  }, [article.id, article.updatedAt]);
  const run = async (name: string, input: unknown): Promise<void> => {
    try {
      await client.http.command(name, input, uuid());
      setComments(await client.http.articleComments(article.id));
    } catch (e: unknown) {
      client.pushError(e instanceof ApiError ? e.code : "network", (e as Error).message);
    }
  };
  return (
    <section>
      <div className="mb-2 flex items-center gap-2 text-[13px] text-muted">Comments{comments?.length ? <span className="tabular-nums">{comments.length}</span> : null}</div>
      <ol className="flex flex-col gap-3">
        {comments?.map((c) => {
          const mine = c.authorId === me?.id || (me?.isAdmin ?? false);
          return (
            <li key={c.id} className="group flex gap-3">
              <Avatar schema={schema} userId={c.authorId} size={28} />
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center gap-2 text-[13px] text-muted">
                  <b className="text-ink">{userName(schema, c.authorId)}</b>
                  <span title={absTime(c.createdAt)}>commented {relTimeAgo(c.createdAt)}</span>
                  {c.updatedAt > c.createdAt + 1000 && <span>(edited)</span>}
                  {mine && editingId !== c.id && (
                    <span className="ml-auto flex gap-1 opacity-0 group-hover:opacity-100">
                      <button className="icon-btn h-6 w-6" title="Edit" onClick={() => setEditingId(c.id)}>
                        <Pencil size={14} />
                      </button>
                      <button className="icon-btn h-6 w-6" title="Delete" onClick={() => confirm("Delete this comment?") && void run("deleteArticleComment", { commentId: c.id })}>
                        <X size={14} />
                      </button>
                    </span>
                  )}
                </div>
                {editingId === c.id ? (
                  <MarkdownEditor
                    initial={c.text}
                    compact
                    autoFocus
                    target={{ articleId: article.id }}
                    onSave={async (md) => {
                      setEditingId(null);
                      if (md && md !== c.text) await run("updateArticleComment", { commentId: c.id, text: md });
                    }}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <MarkdownView text={c.text} target={{ articleId: article.id }} className="text-[14.5px]" />
                )}
              </div>
            </li>
          );
        })}
        {comments && comments.length === 0 && <li className="text-faint">No comments yet.</li>}
      </ol>
      {canComment && schema && me && (
        <div className="mt-4 flex gap-3">
          <Avatar schema={schema} userId={me.id} size={28} />
          <div className="min-w-0 flex-1">
            {composing ? (
              <MarkdownEditor
                initial=""
                placeholder="Write a comment…"
                saveLabel="Comment"
                autoFocus
                compact
                target={{ articleId: article.id }}
                onSave={async (md) => {
                  if (!md) return;
                  await run("addArticleComment", { articleId: article.id, text: md });
                  setComposing(false);
                }}
                onCancel={() => setComposing(false)}
              />
            ) : (
              <button className="field w-full text-left text-faint" onClick={() => setComposing(true)}>
                Write a comment…
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// ----------------------------------------------------------------- new article (draft)

function NewArticle({ tree, projects, initialProjectId, initialParentId, draftId: initialDraftId, drafts, onDraftsChanged }: { tree: Tree; projects: { id: number; key: string; name: string }[]; initialProjectId: number | null; initialParentId: number | null; draftId: number | null; drafts: ArticleDraft[]; onDraftsChanged: () => void }) {
  const schema = useClient((s) => s.schema);
  const navigate = useNavigate();
  const [draftId, setDraftId] = useState<number | null>(initialDraftId);
  const [projectId, setProjectId] = useState<number | null>(initialProjectId ?? projects[0]?.id ?? null);
  const [parentId, setParentId] = useState<number | null>(initialParentId);
  const [summary, setSummary] = useState("");
  const [content, setContent] = useState("");
  const [restored, setRestored] = useState(initialDraftId === null);
  const [busy, setBusy] = useState(false);
  const skipSave = useRef(false);
  // The upload resolver runs outside render, and needs the id the last save produced.
  const draftIdRef = useRef<number | null>(initialDraftId);

  // Reopening a draft from the sidebar: fill the form once the drafts list is here.
  useEffect(() => {
    if (restored || initialDraftId === null) return;
    const d = drafts.find((x) => x.id === initialDraftId);
    if (!d) return;
    setProjectId(d.projectId);
    setParentId(d.parentId);
    setSummary(d.summary);
    setContent(d.content ?? "");
    setRestored(true);
  }, [drafts, initialDraftId, restored]);

  // Autosave as a draft (server-side, like YouTrack) a second after typing stops.
  useEffect(() => {
    if (!restored || busy || projectId === null) return;
    if (!summary.trim() && !content.trim()) return;
    const timer = setTimeout(() => {
      if (skipSave.current) return;
      client.http
        .command<ArticleDraft>("saveArticleDraft", { draftId: draftIdRef.current, projectId, parentId, summary, content: content || null }, uuid())
        .then((d) => {
          draftIdRef.current = d.id;
          if (d.id !== draftId) setDraftId(d.id);
          onDraftsChanged();
        })
        .catch(() => {});
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, content, projectId, parentId]);

  /** Files dropped into an unsaved article go on its draft; publishing moves them to the article. */
  const attachTarget = async (): Promise<{ articleDraftId: number } | null> => {
    if (draftIdRef.current !== null) return { articleDraftId: draftIdRef.current };
    if (projectId === null) return null;
    const d = await client.http.command<ArticleDraft>("saveArticleDraft", { draftId: null, projectId, parentId, summary, content: content || null }, uuid());
    draftIdRef.current = d.id;
    setDraftId(d.id);
    onDraftsChanged();
    return { articleDraftId: d.id };
  };

  const parentOptions: { value: string; label: string }[] = [{ value: "", label: "(top level)" }];
  if (projectId !== null) {
    const walk = (parent: number | null, depth: number): void => {
      for (const c of tree.children.get(childKey(projectId, parent)) ?? []) {
        parentOptions.push({ value: String(c.id), label: `${"  ".repeat(depth)}${c.summary}` });
        walk(c.id, depth + 1);
      }
    };
    walk(null, 0);
  }

  const publish = async (): Promise<void> => {
    if (busy || projectId === null || !summary.trim()) return;
    setBusy(true);
    skipSave.current = true;
    try {
      const a = await client.http.command<Article>("createArticle", { projectId, parentId, summary: summary.trim(), content: content.trim() || null, visibility: null, draftId: draftIdRef.current }, uuid());
      onDraftsChanged();
      navigate(`/article/${articleKey(schema?.projectsById.get(a.projectId)?.key ?? "", a.number)}`);
    } catch (e) {
      skipSave.current = false;
      client.pushError(e instanceof ApiError ? e.code : "network", (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const discard = async (): Promise<void> => {
    if ((summary.trim() || content.trim()) && !confirm("Discard this draft?")) return;
    skipSave.current = true;
    if (draftId !== null) await client.http.command("deleteArticleDraft", { draftId }, uuid()).catch(() => {});
    onDraftsChanged();
    navigate("/articles");
  };

  if (projects.length === 0) return <Centered>You cannot create articles in any project.</Centered>;
  return (
    <div className="flex h-full flex-col px-8 py-4">
      <div className="mb-3 flex items-center gap-3">
        <label className="block">
          <span className="mb-1 block text-xs text-muted">Project</span>
          <SimpleSelect
            value={projectId === null ? "" : String(projectId)}
            options={projects.map((p) => ({ value: String(p.id), label: p.name }))}
            onChange={(v) => {
              setProjectId(v ? Number(v) : null);
              setParentId(null);
            }}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-muted">Parent article</span>
          <SimpleSelect value={parentId === null ? "" : String(parentId)} options={parentOptions} onChange={(v) => setParentId(v ? Number(v) : null)} />
        </label>
        <div className="flex-1" />
        {draftId !== null && <span className="text-xs text-muted">Draft saved</span>}
        <Button primary onClick={() => void publish()} disabled={busy || !summary.trim()}>
          Publish
        </Button>
        <Button onClick={() => void discard()}>Discard</Button>
      </div>
      <Input size={InputSize.FULL} className="mb-3" inputClassName="!text-[22px] !font-semibold" placeholder="Article title" value={summary} onChange={(e) => setSummary(e.target.value)} autoFocus aria-label="Title" />
      <div className="min-h-0 flex-1">
        <MarkdownEditor initial={content} placeholder="Write the article…" hideFooter fill target={attachTarget} onChange={setContent} onSave={() => void publish()} />
      </div>
    </div>
  );
}
